import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ScienceIcon from '@mui/icons-material/Science';
import ModuleHeader from './components/ModuleHeader';
import { useAuth } from './AuthContext';
import {
  ACENTO, AvisoTubito, CAMPOS_LAB, COR, CampoLab, Opcoes, RECIPIENTES, S, SUB, Tubito, TubitoIn, botaoCor, caixa,
  fmtData, gravarSessao, hojeISO, lerSessao, liraaApi, mensagemErro, paraInt, periodo, useCiclosLiraa,
} from './liraaApi';

// Cartao 2 do LIRAa: tubitos (etiquetas) e resultado do laboratorio.
// Formulario NA ORDEM DA ETIQUETA. Trava do motor = caixa vermelha (nao limpa o formulario);
// aviso = caixa amarela (nao impede). Sessao 30, bloco N3 (24/09/2026 - Walber).

const CHAVE_FILTROS = 'tovis-liraa-lab-filtros';

interface Form {
  numero_amostra: string; agente_id: string; data: string; area_id: string; quarteirao: string;
  logradouro: string; numero: string; tipo_imovel: string; recipiente: string; deposito_descricao: string;
  larvas_campo: string; pupas_campo: string; observacao: string;
  lab: Record<CampoLab, string>;
}
const labVazio = (): Record<CampoLab, string> =>
  Object.fromEntries(CAMPOS_LAB.map(c => [c, ''])) as Record<CampoLab, string>;
const formVazio = (): Form => ({
  numero_amostra: '', agente_id: '', data: hojeISO(), area_id: '', quarteirao: '', logradouro: '', numero: '',
  tipo_imovel: 'Outro', recipiente: '', deposito_descricao: '', larvas_campo: '', pupas_campo: '', observacao: '',
  lab: labVazio(),
});
const txt = (v: string | number | null | undefined) => (v === null || v === undefined ? '' : String(v));
const formDe = (t: Tubito): Form => ({
  numero_amostra: txt(t.numero_amostra), agente_id: txt(t.agente_id), data: t.data, area_id: txt(t.area_id),
  quarteirao: txt(t.quarteirao), logradouro: txt(t.logradouro), numero: txt(t.numero), tipo_imovel: t.tipo_imovel,
  recipiente: t.recipiente, deposito_descricao: txt(t.deposito_descricao), larvas_campo: txt(t.larvas_campo),
  pupas_campo: txt(t.pupas_campo), observacao: txt(t.observacao),
  lab: Object.fromEntries(CAMPOS_LAB.map(c => [c, t.lab_analisado ? txt(t[c]) : ''])) as Record<CampoLab, string>,
});

interface Filtros { area: string; agente: string; status: '' | 'pendente' | 'analisado'; }
const lerFiltros = (): Filtros => {
  try { const f = JSON.parse(lerSessao(CHAVE_FILTROS) || '{}'); return { area: f.area || '', agente: f.agente || '', status: f.status || '' }; }
  catch { return { area: '', agente: '', status: '' }; }
};

export default function LiraaLaboratorio({ onVoltar, tubitoId, onTubitoAberto }: {
  onVoltar: () => void;
  tubitoId?: number;
  onTubitoAberto?: () => void;
}) {
  const { token } = useAuth();
  const { ciclos, ciclo, cicloId, setCicloId, recarregar, erro: erroCiclos } = useCiclosLiraa(token);
  const [opcoes, setOpcoes] = useState<Opcoes | null>(null);
  const [form, setForm] = useState<Form>(formVazio);
  const [editando, setEditando] = useState<Tubito | null>(null);
  const [erro, setErro] = useState('');
  const [avisos, setAvisos] = useState<AvisoTubito[]>([]);
  const [ok, setOk] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [lista, setLista] = useState<Tubito[] | null>(null);
  const [erroLista, setErroLista] = useState('');
  const [filtros, setFiltros] = useState<Filtros>(lerFiltros);
  const refImovel = useRef<HTMLInputElement>(null);
  const refLab = useRef<HTMLInputElement>(null);
  const refTopo = useRef<HTMLDivElement>(null);

  useEffect(() => { liraaApi.opcoes(token).then(setOpcoes).catch(e => setErro(mensagemErro(e))); }, [token]);
  useEffect(() => { gravarSessao(CHAVE_FILTROS, JSON.stringify(filtros)); }, [filtros]);

  const carregarLista = useCallback(async () => {
    if (!cicloId) { setLista([]); return; }
    try {
      const f: { area_id?: number; agente_id?: number } = {};
      if (filtros.area) f.area_id = Number(filtros.area);
      if (filtros.agente) f.agente_id = Number(filtros.agente);
      setLista(await liraaApi.tubitos(token, cicloId, f));
      setErroLista('');
    } catch (e) { setErroLista(mensagemErro(e, 'Não foi possível carregar os tubitos.')); }
  }, [token, cicloId, filtros.area, filtros.agente]);
  useEffect(() => { carregarLista(); }, [carregarLista]);

  // Tubito indicado pelo Quadro de Positividade LIRAa: abre para edicao.
  useEffect(() => {
    if (!tubitoId || !cicloId) return;
    liraaApi.tubitos(token, cicloId).then(ts => {
      const t = ts.find(x => x.id === tubitoId);
      if (t) abrirEdicao(t);
      else setErro('O tubito indicado não está neste ciclo. Escolha o ciclo certo no topo.');
      onTubitoAberto?.();
    }).catch(e => setErro(mensagemErro(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tubitoId, cicloId]);

  const set = (k: keyof Omit<Form, 'lab'>, v: string) => setForm(f => ({ ...f, [k]: v }));
  const setLab = (k: CampoLab, v: string) => setForm(f => ({ ...f, lab: { ...f.lab, [k]: v.replace(/[^\d]/g, '') } }));

  const sugerirNumero = async (agenteId: string, base?: Partial<Form>) => {
    if (!agenteId || !cicloId) return;
    try {
      const n = await liraaApi.proximoNumero(token, cicloId, Number(agenteId));
      setForm(f => ({ ...f, ...(base || {}), numero_amostra: String(n) }));
    } catch { /* numero continua editavel */ }
  };

  const escolherAgente = (v: string) => {
    set('agente_id', v);
    if (!editando) sugerirNumero(v);
  };

  function abrirEdicao(t: Tubito, focoLab = false) {
    setEditando(t); setForm(formDe(t)); setErro(''); setAvisos(t.avisos || []); setOk('');
    refTopo.current?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => (focoLab ? refLab : refImovel).current?.focus(), 300);
  }
  const cancelarEdicao = () => { setEditando(null); setForm(formVazio()); setErro(''); setAvisos([]); };

  /** Monta o corpo. comResultado=true manda os 6 campos (vazio = 0). Levanta Error com texto se numero invalido. */
  const montar = (comResultado: boolean): TubitoIn => {
    const num = (rotulo: string, s: string) => {
      const n = paraInt(s);
      if (Number.isNaN(n)) throw new Error(`${rotulo}: use só números inteiros.`);
      return n;
    };
    const corpo: TubitoIn = {
      numero_amostra: num('Nº da amostra', form.numero_amostra) ?? undefined,
      agente_id: form.agente_id ? Number(form.agente_id) : undefined,
      data: form.data || undefined,
      area_id: form.area_id ? Number(form.area_id) : undefined,
      quarteirao: form.quarteirao.trim() || null,
      logradouro: form.logradouro.trim() || null,
      numero: form.numero.trim() || null,
      tipo_imovel: form.tipo_imovel,
      recipiente: form.recipiente || undefined,
      deposito_descricao: form.deposito_descricao.trim() || null,
      larvas_campo: num('Nº larvas (campo)', form.larvas_campo),
      pupas_campo: num('Pupas (campo)', form.pupas_campo),
      observacao: form.observacao.trim() || null,
    };
    if (comResultado) {
      corpo.com_resultado = true;
      for (const c of CAMPOS_LAB) corpo[c] = num('Laboratório', form.lab[c]) ?? 0;
    }
    return corpo;
  };
  const labPreenchido = CAMPOS_LAB.some(c => form.lab[c].trim() !== '');

  const salvar = async (modo: 'pendente' | 'resultado' | 'proximo' | 'alteracoes') => {
    setErro(''); setOk(''); setAvisos([]);
    let corpo: TubitoIn;
    const comResultado = modo === 'resultado' || (modo === 'proximo' && labPreenchido)
      || (modo === 'alteracoes' && !!editando?.lab_analisado);
    try { corpo = montar(comResultado); } catch (e: any) { setErro(e.message); return; }
    setSalvando(true);
    try {
      const t = editando
        ? await liraaApi.editarTubito(token, editando.id, corpo)
        : await liraaApi.criarTubito(token, cicloId!, corpo);
      setAvisos(t.avisos || []);
      setOk(`Tubito nº ${t.numero_amostra} de ${t.agente_nome} salvo ${t.lab_analisado ? 'com resultado' : 'como pendente'}.`);
      const manter: Partial<Form> = { agente_id: form.agente_id, data: form.data, area_id: form.area_id };
      if (modo === 'proximo') Object.assign(manter, { quarteirao: form.quarteirao, logradouro: form.logradouro });
      setEditando(null);
      setForm({ ...formVazio(), ...manter });
      await sugerirNumero(form.agente_id, manter);
      if (modo === 'proximo') setTimeout(() => refImovel.current?.focus(), 50);
      carregarLista(); recarregar();
    } catch (e) {
      setErro(mensagemErro(e));   // formulario NAO e limpo
    } finally { setSalvando(false); }
  };

  const desfazer = async (t: Tubito) => {
    if (!window.confirm(`Desfazer o resultado do tubito nº ${t.numero_amostra} (${t.agente_nome})? Ele volta a ficar pendente.`)) return;
    try { await liraaApi.desfazerResultado(token, t.id); carregarLista(); recarregar(); }
    catch (e) { setErroLista(mensagemErro(e)); }
  };
  const excluir = async (t: Tubito) => {
    if (!window.confirm(`Excluir o tubito nº ${t.numero_amostra} de ${t.agente_nome}? Isso não pode ser desfeito.`)) return;
    try { await liraaApi.excluirTubito(token, t.id); if (editando?.id === t.id) cancelarEdicao(); carregarLista(); recarregar(); }
    catch (e) { setErroLista(mensagemErro(e)); }
  };

  const visiveis = useMemo(() => (lista || []).filter(t =>
    !filtros.status || (filtros.status === 'analisado' ? t.lab_analisado : !t.lab_analisado)), [lista, filtros.status]);
  const nPend = (lista || []).filter(t => !t.lab_analisado).length;
  const nAnal = (lista || []).length - nPend;

  const campo = (rotulo: string, el: React.ReactNode, largura = '1 1 150px') => (
    <label style={{ flex: largura, minWidth: 0 }}><span style={S.rotulo}>{rotulo}</span>{el}</label>
  );
  const inputLab = (c: CampoLab, primeiro = false) => (
    <input ref={primeiro ? refLab : undefined} style={{ ...S.campo, width: 64, textAlign: 'center' }} inputMode="numeric"
      value={form.lab[c]} placeholder="0" onChange={e => setLab(c, e.target.value)} aria-label={c} />
  );

  return (
    <div style={S.pagina}>
      <ModuleHeader
        titulo="Laboratório LIRAa"
        subtitulo="Tubitos do LIRAa: pendentes e resultados"
        icone={<ScienceIcon />}
        accentColor={ACENTO.laboratorio}
        onVoltar={onVoltar}
        chips={[
          { label: 'Ciclo', value: ciclo?.nome || '—' },
          { label: 'Período', value: periodo(ciclo) },
          { label: 'Pendentes', value: ciclo ? ciclo.pendentes : '—' },
        ]}
      />
      <div style={S.miolo} ref={refTopo}>
        <div style={{ ...S.card, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ flex: '1 1 260px' }}><span style={S.rotulo}>🔁 Ciclo LIRAa</span>
            <select style={S.campo} value={cicloId ?? ''} onChange={e => { setCicloId(Number(e.target.value) || null); cancelarEdicao(); }} disabled={!ciclos?.length}>
              {!ciclos?.length && <option value="">— nenhum ciclo —</option>}
              {ciclos?.map(c => <option key={c.id} value={c.id}>{c.nome}{c.em_andamento ? ' (em andamento)' : ''}</option>)}
            </select></label>
        </div>
        {erroCiclos && <div style={caixa('vermelho')}>{erroCiclos}</div>}
        {ciclos !== null && ciclos.length === 0 && (
          <div style={caixa('cinza')}>Nenhum ciclo ainda. Crie o ciclo no cartão "LIRAa — Ciclo e Equipe".</div>
        )}

        {ciclo && (
          <div style={{ ...S.card, borderLeft: `6px solid ${editando ? COR.amarelo.forte : ACENTO.laboratorio}` }}>
            <h3 style={S.tituloSecao}>
              {editando ? `Editando o tubito nº ${editando.numero_amostra} de ${editando.agente_nome}` : 'Novo tubito (na ordem da etiqueta)'}
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {campo('Nº da amostra', <input style={S.campo} inputMode="numeric" value={form.numero_amostra} onChange={e => set('numero_amostra', e.target.value)} />, '0 1 110px')}
              {campo('Agente', (
                <select style={S.campo} value={form.agente_id} onChange={e => escolherAgente(e.target.value)}>
                  <option value="">Escolha…</option>
                  {opcoes?.agentes.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
                  {editando && !opcoes?.agentes.some(a => a.id === editando.agente_id) && <option value={editando.agente_id}>{editando.agente_nome}</option>}
                </select>), '1 1 170px')}
              {campo('Data', <input style={S.campo} type="date" value={form.data} onChange={e => set('data', e.target.value)} />, '0 1 160px')}
              {campo('Bairro (área)', (
                <select style={S.campo} value={form.area_id} onChange={e => set('area_id', e.target.value)}>
                  <option value="">Escolha…</option>
                  {opcoes?.areas.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
                  {editando && !opcoes?.areas.some(a => a.id === editando.area_id) && <option value={editando.area_id}>{editando.area_nome}</option>}
                </select>), '1 1 170px')}
              {campo('Quart.', <input style={S.campo} value={form.quarteirao} onChange={e => set('quarteirao', e.target.value)} />, '0 1 90px')}
              {campo('Rua', <input style={S.campo} value={form.logradouro} maxLength={200} onChange={e => set('logradouro', e.target.value)} />, '2 1 220px')}
              {campo('Imóvel nº', <input ref={refImovel} style={S.campo} value={form.numero} maxLength={40} onChange={e => set('numero', e.target.value)} placeholder="ou S/N" />, '0 1 100px')}
              {campo('Tipo de imóvel', (
                <select style={S.campo} value={form.tipo_imovel} onChange={e => set('tipo_imovel', e.target.value)}>
                  <option value="Outro">Outro</option><option value="TB">TB (terreno baldio)</option>
                </select>), '0 1 170px')}
              {campo('Código do depósito', (
                <select style={S.campo} value={form.recipiente} onChange={e => set('recipiente', e.target.value)}>
                  <option value="">Escolha…</option>
                  {RECIPIENTES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>), '0 1 130px')}
              {campo('Depósito (descrição, opcional)', <input style={S.campo} value={form.deposito_descricao} maxLength={120} onChange={e => set('deposito_descricao', e.target.value)} placeholder="ex.: caixa d'água" />, '1 1 200px')}
              {campo('Nº larvas (campo)', <input style={S.campo} inputMode="numeric" value={form.larvas_campo} onChange={e => set('larvas_campo', e.target.value)} />, '0 1 110px')}
              {campo('Pupas (campo)', <input style={S.campo} inputMode="numeric" value={form.pupas_campo} onChange={e => set('pupas_campo', e.target.value)} />, '0 1 100px')}
              {campo('Observação', <input style={S.campo} value={form.observacao} onChange={e => set('observacao', e.target.value)} />, '2 1 240px')}
            </div>

            <div style={{ marginTop: 14, padding: '10px 12px', background: '#f5f3ff', borderRadius: 10 }}>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>🔬 Laboratório <span style={{ fontWeight: 400, fontSize: 13, color: SUB }}>— só <i>Ae. aegypti</i> e <i>Ae. albopictus</i> contam como positivo; vazio = 0</span></div>
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                <div><div style={S.rotulo}><i>Ae. aegypti</i> — L / P</div><div style={{ display: 'flex', gap: 6 }}>{inputLab('lab_aeg_larvas', true)}{inputLab('lab_aeg_pupas')}</div></div>
                <div><div style={S.rotulo}><i>Ae. albopictus</i> — L / P</div><div style={{ display: 'flex', gap: 6 }}>{inputLab('lab_alb_larvas')}{inputLab('lab_alb_pupas')}</div></div>
                <div><div style={S.rotulo}>Outros — L / P</div><div style={{ display: 'flex', gap: 6 }}>{inputLab('lab_outros_larvas')}{inputLab('lab_outros_pupas')}</div></div>
              </div>
            </div>

            {erro && <div style={{ ...caixa('vermelho'), marginTop: 12, fontWeight: 700 }}>⛔ {erro}</div>}
            {ok && <div style={{ ...caixa('verde'), marginTop: 12 }}>✓ {ok}</div>}
            {avisos.length > 0 && (
              <div style={{ ...caixa('amarelo'), marginTop: 12 }}>
                <div style={{ fontWeight: 800, marginBottom: 4 }}>⚠️ {avisos.length} aviso(s) — o tubito foi salvo, mas confira:</div>
                {avisos.map((a, i) => (
                  <div key={i} style={{ marginTop: 6 }}>
                    <b>{a.explicacao.titulo || a.regra}</b> <span style={{ fontSize: 12 }}>({a.regra})</span><br />
                    {a.mensagem}
                    {a.explicacao.corrigir && <div style={{ fontSize: 13 }}><b>Como corrigir:</b> {a.explicacao.corrigir}</div>}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              {!editando ? (
                <>
                  <button style={S.botao} onClick={() => salvar('pendente')} disabled={salvando}>Salvar pendente</button>
                  <button style={botaoCor(COR.verde.forte)} onClick={() => salvar('resultado')} disabled={salvando}>Salvar com resultado</button>
                  <button style={botaoCor(ACENTO.laboratorio)} onClick={() => salvar('proximo')} disabled={salvando}>Salvar e próximo ▸</button>
                </>
              ) : (
                <>
                  <button style={botaoCor(ACENTO.laboratorio)} onClick={() => salvar('alteracoes')} disabled={salvando}>Salvar alterações</button>
                  <button style={botaoCor(COR.verde.forte)} onClick={() => salvar('resultado')} disabled={salvando}>Salvar com resultado</button>
                  <button style={S.botao} onClick={cancelarEdicao} disabled={salvando}>Cancelar edição</button>
                </>
              )}
              {salvando && <span style={{ alignSelf: 'center', color: SUB }}>Salvando…</span>}
            </div>
          </div>
        )}

        {ciclo && (
          <div style={S.card}>
            <h3 style={S.tituloSecao}>Tubitos do ciclo</h3>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
              <label style={{ flex: '1 1 160px' }}><span style={S.rotulo}>Área</span>
                <select style={S.campo} value={filtros.area} onChange={e => setFiltros(f => ({ ...f, area: e.target.value }))}>
                  <option value="">Todas</option>{opcoes?.areas.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
                </select></label>
              <label style={{ flex: '1 1 160px' }}><span style={S.rotulo}>Agente</span>
                <select style={S.campo} value={filtros.agente} onChange={e => setFiltros(f => ({ ...f, agente: e.target.value }))}>
                  <option value="">Todos</option>{opcoes?.agentes.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
                </select></label>
              <label style={{ flex: '1 1 160px' }}><span style={S.rotulo}>Status</span>
                <select style={S.campo} value={filtros.status} onChange={e => setFiltros(f => ({ ...f, status: e.target.value as Filtros['status'] }))}>
                  <option value="">Todos</option><option value="pendente">Pendentes</option><option value="analisado">Analisados</option>
                </select></label>
              <div style={{ fontWeight: 800, fontSize: 15, fontVariantNumeric: 'tabular-nums', padding: '8px 0' }}>
                <span style={{ color: nPend ? COR.amarelo.texto : SUB }}>{nPend} pendentes</span> · <span style={{ color: COR.verde.texto }}>{nAnal} analisados</span>
              </div>
            </div>
            {erroLista && <div style={caixa('vermelho')}>{erroLista}</div>}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  {['Nº', 'Agente', 'Data', 'Área', 'Quart.', 'Endereço', 'Tipo', 'Dep.', 'Aegypti L/P', 'Albopictus L/P', 'Outros L/P', 'Situação', 'Ações'].map(h => <th key={h} style={S.th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {lista === null && <tr><td colSpan={13} style={S.td}>Carregando…</td></tr>}
                  {lista !== null && visiveis.length === 0 && <tr><td colSpan={13} style={{ ...S.td, color: SUB }}>Nenhum tubito nesta seleção.</td></tr>}
                  {visiveis.map(t => {
                    const lp = (l: number | null, p: number | null) => (t.lab_analisado ? `${l ?? 0} / ${p ?? 0}` : '—');
                    return (
                      <tr key={t.id} style={{ background: !t.lab_analisado ? COR.amarelo.fundo : editando?.id === t.id ? '#ede9fe' : undefined }}>
                        <td style={{ ...S.td, fontWeight: 800 }}>{t.numero_amostra}</td>
                        <td style={{ ...S.td, textAlign: 'left' }}>{t.agente_nome}</td>
                        <td style={S.td}>{fmtData(t.data)}</td>
                        <td style={{ ...S.td, textAlign: 'left' }}>{t.area_nome}</td>
                        <td style={S.td}>{t.quarteirao || '—'}</td>
                        <td style={{ ...S.td, textAlign: 'left', whiteSpace: 'normal', minWidth: 140 }}>{[t.logradouro, t.numero].filter(Boolean).join(', ') || '—'}</td>
                        <td style={S.td}>{t.tipo_imovel}</td>
                        <td style={{ ...S.td, fontWeight: 700 }} title={t.deposito_descricao || undefined}>{t.recipiente}</td>
                        <td style={{ ...S.td, color: t.positivo_aeg ? COR.vermelho.forte : undefined, fontWeight: t.positivo_aeg ? 800 : 400 }}>{lp(t.lab_aeg_larvas, t.lab_aeg_pupas)}</td>
                        <td style={{ ...S.td, color: t.positivo_alb ? COR.vermelho.forte : undefined, fontWeight: t.positivo_alb ? 800 : 400 }}>{lp(t.lab_alb_larvas, t.lab_alb_pupas)}</td>
                        <td style={S.td}>{lp(t.lab_outros_larvas, t.lab_outros_pupas)}</td>
                        <td style={S.td}>
                          {t.lab_analisado
                            ? <span style={{ color: COR.verde.texto, fontWeight: 700 }}>✓ analisado</span>
                            : <span style={{ color: COR.amarelo.texto, fontWeight: 800 }}>pendente</span>}
                          {(t.avisos?.length ?? 0) > 0 && (
                            <span title={t.avisos!.map(a => `${a.regra}: ${a.mensagem}`).join('\n')} style={{ marginLeft: 6, color: COR.amarelo.forte, cursor: 'help' }}>⚠{t.avisos!.length}</span>
                          )}
                        </td>
                        <td style={S.td}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                            <button style={S.botaoMini} onClick={() => abrirEdicao(t)}>Editar</button>
                            {!t.lab_analisado && <button style={{ ...S.botaoMini, borderColor: COR.verde.forte, color: COR.verde.texto }} onClick={() => abrirEdicao(t, true)}>Lançar resultado</button>}
                            {t.lab_analisado && <button style={S.botaoMini} onClick={() => desfazer(t)}>Desfazer resultado</button>}
                            <button style={{ ...S.botaoMini, borderColor: COR.vermelho.forte, color: COR.vermelho.texto }} onClick={() => excluir(t)}>Excluir</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
